import { useCallback, useEffect, useMemo, useState } from "react";
import type { HealthResponse, Recall, RecallListResponse } from "../shared/types";
import { getCaptionRecalls, getHealth, getRecall, getRecalls, type Filters } from "./api";
import { RecallVideo } from "./RecallVideo";
import { BarcodeScanner } from "./BarcodeScanner";

const initialFilters: Filters = { query: "", agency: "", source: "", urgent: "" };

function formatDate(value: string): string {
  const date = new Date(value);
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "America/New_York",
  }).format(date);
}

function sourceName(recall: Recall): string {
  if (recall.sourceType === "usda_fsis") return "USDA FSIS";
  if (recall.sourceType === "fda_enforcement") return "FDA ENFORCEMENT";
  return "FDA WARNING";
}

function RecallCard({ recall, index }: { recall: Recall; index: number }) {
  return (
    <article className="recall-card">
      <div className="recall-index" aria-hidden="true">
        {String(index + 1).padStart(2, "0")}
      </div>
      <div className="recall-main">
        <div className="recall-meta">
          <span>{sourceName(recall)}</span>
          <span>{formatDate(recall.publishedAt)}</span>
          {recall.classification && <span>{recall.classification}</span>}
          {!recall.urgent && <span>WEEKLY RECORD</span>}
        </div>
        <h2>
          <a href={`/recalls/${recall.id}`}>{recall.title}</a>
        </h2>
        {(recall.reason || recall.summary) && (
          <p>{recall.reason ?? recall.summary}</p>
        )}
        <div className="recall-bottom">
          <span>{recall.company ?? recall.productDescription ?? "Official recall notice"}</span>
          <a href={recall.sourceUrl} target="_blank" rel="noreferrer">
            SOURCE ↗
          </a>
        </div>
      </div>
    </article>
  );
}

function Detail({ id }: { id: string }) {
  const [recall, setRecall] = useState<Recall | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    getRecall(id, controller.signal).then(setRecall).catch((reason: unknown) => {
      if (!controller.signal.aborted) setError(reason instanceof Error ? reason.message : String(reason));
    });
    return () => controller.abort();
  }, [id]);

  if (error) return <main className="detail"><a href="/">← ALL RECALLS</a><h1>Recall unavailable.</h1><p>{error}</p></main>;
  if (!recall) return <main className="detail"><p>Loading official record…</p></main>;

  const fields = [
    ["Agency", recall.agency],
    ["Classification", recall.classification],
    ["Status", recall.status],
    ["Company", recall.company],
    ["Product", recall.productDescription],
    ["Reason", recall.reason],
    ["Distribution", recall.distribution],
    ["Recall date", recall.recallDate],
  ].filter((field): field is [string, string] => Boolean(field[1]));

  return (
    <main className="detail">
      <a className="back-link" href="/">← ALL RECALLS</a>
      <div className="detail-kicker">{sourceName(recall)} / {formatDate(recall.publishedAt)}</div>
      <h1>{recall.title}</h1>
      {recall.summary && <p className="detail-summary">{recall.summary}</p>}
      <dl>
        {fields.map(([label, value]) => (
          <div key={label}><dt>{label}</dt><dd>{value}</dd></div>
        ))}
      </dl>
      <a className="source-button" href={recall.sourceUrl} target="_blank" rel="noreferrer">
        READ THE OFFICIAL SOURCE ↗
      </a>
    </main>
  );
}

function App() {
  const detailId = window.location.pathname.match(/^\/recalls\/([a-z0-9_]+)$/i)?.[1];
  const [filters, setFilters] = useState<Filters>(initialFilters);
  const [data, setData] = useState<RecallListResponse | null>(null);
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [items, setItems] = useState<Recall[]>([]);
  const [captionRecalls, setCaptionRecalls] = useState<Recall[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback((cursor?: string, append = false) => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    getRecalls(filters, cursor, controller.signal)
      .then((response) => {
        setData(response);
        setItems((current) => (append ? [...current, ...response.items] : response.items));
      })
      .catch((reason: unknown) => {
        if (!controller.signal.aborted) setError(reason instanceof Error ? reason.message : String(reason));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return controller;
  }, [filters]);

  useEffect(() => {
    let controller: AbortController | undefined;
    const timer = window.setTimeout(() => {
      controller = load();
    }, 200);
    return () => {
      window.clearTimeout(timer);
      controller?.abort();
    };
  }, [load]);

  useEffect(() => {
    let controller: AbortController | undefined;
    const refresh = () => {
      controller?.abort();
      controller = new AbortController();
      getCaptionRecalls(controller.signal)
        .then((response) => setCaptionRecalls(response.items))
        .catch(() => undefined);
    };
    refresh();
    const timer = window.setInterval(refresh, 5 * 60_000);
    return () => {
      controller?.abort();
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    getHealth(controller.signal).then(setHealth).catch(() => undefined);
    const timer = window.setInterval(() => {
      load();
      getHealth().then(setHealth).catch(() => undefined);
    }, 5 * 60_000);
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [load]);

  const healthLabel = useMemo(() => {
    if (!health) return "CHECKING SOURCES";
    return health.ok ? "ALL SOURCES LIVE" : "SOURCE DEGRADED";
  }, [health]);

  if (detailId) return <Detail id={detailId} />;

  return (
    <main>
      <header className="hero">
        <div className="hero-topline">
          <span>UNITED STATES / OFFICIAL RECALL AGGREGATOR</span>
          <span className={health?.ok ? "status live" : "status"}><i />{healthLabel}</span>
        </div>
        <nav className="visitor-actions" aria-label="Check a product">
          <a className="check-product" href="#barcode-check">CHECK A PRODUCT BARCODE ↓</a>
          <a href="#recall-search">Search recalls</a>
        </nav>
        <div className="hero-main">
          <h1>all the food<br />is poison</h1>
          <RecallVideo recalls={captionRecalls} />
        </div>
        <div className="hero-links">
          <p>FDA + USDA FSIS. Checked every five minutes. No paywall, no bullshit.</p>
          <nav aria-label="Developer feeds">
            <a href="/feed.xml">ATOM ↗</a>
            <a href="/feed.json">JSON FEED ↗</a>
            <a href="/api/v1/recalls">API ↗</a>
            <a href="/openapi.json">OPENAPI ↗</a>
          </nav>
        </div>
      </header>

      <BarcodeScanner />

      <section className="filters" aria-label="Recall filters" id="recall-search" tabIndex={-1}>
        <label className="search-field">
          <span>SEARCH</span>
          <input
            type="search"
            value={filters.query}
            placeholder="SALMONELLA, MILK, MANGOES…"
            onChange={(event) => setFilters((current) => ({ ...current, query: event.target.value }))}
          />
        </label>
        <label>
          <span>AGENCY</span>
          <select value={filters.agency} onChange={(event) => setFilters((current) => ({ ...current, agency: event.target.value }))}>
            <option value="">ALL</option>
            <option value="FDA">FDA</option>
            <option value="USDA FSIS">USDA FSIS</option>
          </select>
        </label>
        <label>
          <span>RECORD TYPE</span>
          <select value={filters.source} onChange={(event) => setFilters((current) => ({ ...current, source: event.target.value }))}>
            <option value="">ALL</option>
            <option value="fda_public_warning">FDA WARNINGS</option>
            <option value="fda_enforcement">FDA ENFORCEMENT</option>
            <option value="usda_fsis">USDA FSIS</option>
          </select>
        </label>
        <label>
          <span>URGENCY</span>
          <select value={filters.urgent} onChange={(event) => setFilters((current) => ({ ...current, urgent: event.target.value }))}>
            <option value="">ALL RECORDS</option>
            <option value="true">PUBLIC WARNINGS</option>
            <option value="false">WEEKLY RECORDS</option>
          </select>
        </label>
      </section>

      <section className="feed-heading">
        <h2>LATEST RECALLS</h2>
        <span>{data ? `${data.total.toLocaleString()} RECORDS` : "LOADING"}</span>
      </section>

      <section className="recall-list" aria-live="polite">
        {items.map((recall, index) => <RecallCard key={recall.id} recall={recall} index={index} />)}
        {!loading && items.length === 0 && <p className="empty">No recalls match those filters.</p>}
        {error && <p className="error">The feed could not load: {error}</p>}
      </section>

      {data?.nextCursor && (
        <button className="load-more" disabled={loading} onClick={() => load(data.nextCursor ?? undefined, true)}>
          {loading ? "LOADING…" : "LOAD MORE ↓"}
        </button>
      )}

      <footer>
        <p>This is an independent index of official government records, not medical advice. Always verify details at the linked FDA or USDA source.</p>
        <p><a href="https://concept.org.gg">CONCEPT ↗</a></p>
      </footer>
    </main>
  );
}

export default App;
